const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/Forms/components/hooks.ts';
import { FieldRef, Form, FormField, FormRef, FormSubmitHandler, SetFormFieldValueFunction } from '../defs';
import { ReactNode, RefObject, useContext, useEffect, useImperativeHandle, useReducer, useRef, useState } from 'react';
import textMap from './translations';
import { TextInput, useOptionalText } from '../../../utils/intlUtils';
import { FormContext } from './context';
import { KeyValuePair } from '../../../defs/generalDefs';
import _ from 'lodash';

export const useInitForm = (
    onSubmit?: FormSubmitHandler,
    data?: KeyValuePair
): [formRef: RefObject<FormRef | undefined | null>, dataRef: RefObject<KeyValuePair | null>] => {
    const formRef = useRef<FormRef>(undefined);

    const dataRef = useRef<KeyValuePair>(_.cloneDeep(data ?? {}));
    // const [initData] = useState(_.cloneDeep(dataRef.current));
    const fieldsRef = useRef<KeyValuePair<RefObject<FieldRef | null>>>({});
    const [forcedUpdateCount, forceUpdate] = useReducer(accumulator => accumulator + 1, 0);

    useImperativeHandle(formRef, (): FormRef => {
        const fields = fieldsRef.current;

        const getFields = () => _.compact(Object.values(fields).map(field => field.current));

        const getField = (path: string) => {
            if (fields[path]?.current) {
                return fields[path].current;
            }
            return undefined;
        };

        const isValid = () =>
            getFields()
                .map(field => field.isValid())
                .every(isValid => isValid);

        const validate = () =>
            getFields()
                .map(field => field.validate())
                .every(isValid => isValid);

        const submitWithoutValidation = () => {
            onSubmit?.(dataRef.current);
        };

        const submit = () => {
            const isValid = validate();
            if (isValid) {
                submitWithoutValidation();
            }
            return isValid;
        };

        // For internal use only (only form related components should use this)
        const __setFieldValue: SetFormFieldValueFunction = (path: string, value: any) => {
            if (_.get(dataRef.current, path) !== value) {
                _.set(dataRef.current, path, value);
                forceUpdate();
            }
        };

        const setFieldValue = (path: string, value: any) => {
            const field = getField(path);
            if (field) {
                __setFieldValue(path, value);
                field.setValue(value);
            } else {
                throw new Error(`Form: Trying to set value of field that does not exist: "${path}"`);
            }
        };

        const setData = (data: KeyValuePair) => {
            dataRef.current = _.cloneDeep(data);

            Object.keys(fieldsRef.current).forEach(key => {
                setFieldValue(key, _.get(data, key));
            });
            forceUpdate();
        };

        return {
            // For internal use only (only form related components should use this)
            __addField: (path: string, formFieldRef: RefObject<FieldRef | null>) => {
                if (fieldsRef.current[path]) {
                    throw new Error(`Form: Field with path "${path}" already exists`);
                }

                if (formFieldRef.current) {
                    fieldsRef.current[path] = formFieldRef;
                    _.set(dataRef.current, path, formFieldRef.current.getValue());
                }
            },
            // For internal use only (only form related components should use this)
            __removeField: path => {
                delete fieldsRef.current[path];
                _.unset(dataRef.current, path);
            },
            // For internal use only (only form related components should use this)
            __setFieldValue,
            isChanged: () =>
                getFields()
                    .map(field => field.isChanged())
                    .some(isChanged => isChanged),
            isValid,
            setData,
            setFieldValue,
            submit,
            toString: () =>
                getFields()
                    .map(field => field.toString())
                    .join(' | '),
            validate,
        };
    }, []);

    return [formRef, dataRef];
};

export const useForm = (): Form => {
    const [forcedUpdateCount, forceUpdate] = useReducer(accumulator => accumulator + 1, 0);

    useEffect(() => {
        // force an update on first render to make sure that the initialized form is available in this context
        // fixes TMOB-7599
        forceUpdate();
    }, []);

    const context = useContext(FormContext);
    const formRef = context?.formRef;
    const dataRef = context?.dataRef;

    const fallbackIsChanged = () => false;
    const fallbackIsValid = () => true;

    const fallbackSetValue: SetFormFieldValueFunction = (path: string, value: any) => {
        console.error(
            `setValue("${path}", value) is not available right now. The component is not inside a Form (or somehow the context isn't up-to-date.`
        );
    };

    const fallbackSubmit = () => {
        console.error('Cannot submit form at this time, because the form has not been initialized yet');
    };

    const fallbackToString = () => 'Form is not initialized yet';

    const data = dataRef?.current ?? {};
    const formCurrent = formRef?.current;

    const getData = () => data;
    const isChanged = formCurrent?.isChanged ?? fallbackIsChanged;
    const isValid = formCurrent?.isValid ?? fallbackIsValid;
    const setFieldValue = formCurrent?.setFieldValue ?? fallbackSetValue;
    const submit = formCurrent?.submit ?? fallbackSubmit;
    const toString = formCurrent?.toString ?? fallbackToString;

    return { getData, isChanged, isValid, setFieldValue, submit, toString };
};

export const useFormFieldBehavior = <ValueType>(
    props: FormField<ValueType>,
    defaultValue: ValueType,
    // dependencyValueFunction is required to avoid changing data that doesn't necessarily change, like an object or an array
    dependencyValueFunction?: (value: ValueType) => string | boolean | number | null | undefined
): [value: ValueType, setValue: (value: ValueType) => void, errorMessage: ReactNode] => {
    const { checkValidity = (value: ValueType) => undefined, path, required, requiredText } = props;

    const context = useContext(FormContext);
    const formRef = context?.formRef;
    const dataRef = context?.dataRef;
    const valueFromForm = dataRef?.current && path ? _.get(dataRef.current, path) : undefined;

    const ref = useRef<FieldRef<ValueType>>(null);

    // If an array or object is passed as default and if useState is not used, the useEffect for propsValue
    // would otherwise assume that it has a new value and would keep thinking a change happens.....
    const defaultValueRef = useRef<ValueType>(props.value ?? valueFromForm ?? defaultValue);

    const propsValue = props.value ?? defaultValueRef.current;
    const propsValueDependency = dependencyValueFunction ? dependencyValueFunction(propsValue) : propsValue;

    const [value, setValue] = useState(propsValue);
    const valueDependency = dependencyValueFunction ? dependencyValueFunction(value) : value;

    const [errorMessage, setErrorMessage] = useState<TextInput>(undefined);

    const hasValue =
        value !== '' && value !== undefined && value !== null && !(Array.isArray(value) && value.length === 0);

    let firstRender = false;

    useEffect(() => {
        firstRender = true;
    }, []);

    useEffect(() => {
        if ((hasValue || !required) && !checkValidity(value)) {
            setErrorMessage(undefined);
        }
    }, [hasValue, required, value]);

    useEffect(() => {
        if (formRef?.current && path) {
            formRef.current.__setFieldValue(path, value);
        }

        if (firstRender) return;

        props.onChange?.(value);
    }, [valueDependency]);

    // To make the component update in case the value is updated from outside
    useEffect(() => {
        setValue(propsValue);
    }, [propsValueDependency]);

    useImperativeHandle(
        ref,
        () => ({
            getPath: () => path,
            getValue: () => value,
            isChanged: () => value !== defaultValueRef.current,
            isValid: () => {
                if ((required && !hasValue) || checkValidity(value)) {
                    return false;
                }
                return true;
            },
            setValue,
            toString: () => `${path}: "${value}" (${typeof value})`,
            validate: () => {
                if (required && !hasValue) {
                    setErrorMessage(requiredText ?? textMap.validationFieldRequired);
                    return false;
                }

                const validationError = checkValidity(value);
                if (validationError) {
                    setErrorMessage(validationError);
                    return false;
                }

                return true;
            },
        }),
        [required, valueDependency]
    );

    useEffect(() => {
        if (formRef?.current && path) {
            formRef.current.__addField(path, ref);

            // Accessing removeFormField inside the return is not possible because formContext.formRef.current is already null
            const removeFormField = formRef.current.__removeField;

            return () => {
                removeFormField(path);
            };
        }
    }, []);

    const translatedErrorMessage = useOptionalText(errorMessage || props.errorMessage);

    return [value, setValue, translatedErrorMessage];
};
